import BrandedSuccessLottie from "@/components/ui/BrandedSuccessLottie";
import PageTransition from "@/components/ui/PageTransition";
import { Button } from "@/components/ui/button/button";
import Spinner from "@/components/ui/spinner";
import Typography from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import {
	useResendVerificationEmail,
	useResendVerificationEmailOnReturn,
} from "@/features/email-verification/useResendVerificationEmail";
import { routes } from "@/router";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { VerifyEmailChangeEmailSection } from "./VerifyEmailChangeEmailSection";

/** intent "sign_up" = email just sent; "returning" = user came back, no email sent. Default to "returning" when unknown. */
export default function VerifyEmailPage() {
	const navigate = useNavigate();
	const { toast } = useToast();
	const resendMutation = useResendVerificationEmail();
	const { intent } = useSearch({ from: routes.signUp.verifyAccount.id });
	const isReturning = intent === "returning";

	const { user } = useAuth();

	// TanStack Query: auto-send when returning (enabled = intent=returning + user exists)
	useResendVerificationEmailOnReturn(isReturning, user?.id);

	const handleResend = () => {
		resendMutation.mutate(
			{
				guest_id: user.id,
				email_verification_callback: `${window.location.origin}/verify-email`,
			},
			{
				onSuccess: () => {
					toast({
						title: "Verification email sent",
						description:
							"Please check your inbox and follow the link to verify your email.",
					});
				},
				onError: () => {
					toast({
						title: "Verification failed",
						description: "Failed to send verification email. Please try again.",
						variant: "destructive",
					});
				},
			},
		);
	};

	return (
		<PageTransition>
			<div className="flex flex-col items-center justify-center px-6 py-8 bg-white">
				<div className="flex flex-col items-center max-w-md w-full text-center">
					{/* Success Lottie */}
					<div className="max-w-56 w-full mx-auto mb-4">
						<BrandedSuccessLottie />
					</div>

					{/* Headline */}
					<Typography
						variant="h3"
						color="primary"
						weight="semibold"
						className="mb-3"
					>
						Check your email
					</Typography>

					{/* Body */}
					<Typography
						variant="p"
						color="primary"
						className="mb-2 leading-relaxed"
					>
						We've sent you a verification link. Click the link in the email to
						verify your account and continue.
					</Typography>
					<Typography
						variant="p"
						color="primary-light"
						className="text-sm mb-6"
					>
						Didn't receive the email? Check your spam or junk folder.
					</Typography>

					{/* Actions */}
					<div className="flex flex-col items-center gap-3 mt-4 w-full">
						<VerifyEmailChangeEmailSection
							resendEmailButton={
								<Button
									variant="default"
									className="w-full max-w-[300px]"
									onClick={handleResend}
									disabled={resendMutation.isPending}
								>
									{resendMutation.isPending ? (
										<div className="flex items-center justify-center">
											<Spinner />
										</div>
									) : (
										"Resend verification email"
									)}
								</Button>
							}
						/>
						<Button
							type="button"
							variant={"link"}
							onClick={() =>
								navigate({ to: "/", search: { promo: undefined } })
							}
						>
							Back to sign in
						</Button>
					</div>
				</div>
			</div>
		</PageTransition>
	);
}
